import React, { useState, useMemo } from "react";
import { Landmark, Handshake, TrendingUp, QrCode } from "lucide-react";
import { SectionHeader } from "./Rates";
import { LEASE_STEPS, WHATSAPP_NUMBER } from "../mock";

const ICONS = { Landmark, Handshake, TrendingUp };

const BuyLease = () => {
  const [qty, setQty] = useState("");
  const [rate, setRate] = useState("244");

  const calc = useMemo(() => {
    const q = parseFloat(qty) || 0;
    const r = parseFloat(rate) || 0;
    const value = q * r;
    const making = value * 0.03;
    return { value, making, total: value + making };
  }, [qty, rate]);

  const fmt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

  return (
    <section id="lease" className="relative py-24 grid-bg">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="Buy & Lease Silver" title="Buy Silver Today, Lease It From Tomorrow" sub="Purchase 999 hallmarked silver directly from us, then place it on lease — we handle the storage and pay you fixed returns while your metal keeps working." />

        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {LEASE_STEPS.map((s) => {
            const Icon = ICONS[s.icon];
            return (
              <div key={s.n} className="relative border border-white/8 bg-white/[0.02] p-8 card-glow overflow-hidden">
                <span className="absolute top-4 right-6 font-display text-7xl text-white/5">{s.n}</span>
                <div className="h-14 w-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold">
                  <Icon size={26} />
                </div>
                <h3 className="font-display text-2xl text-white mt-6">{s.title}</h3>
                <p className="font-body text-gray-400 mt-3 leading-relaxed">{s.desc}</p>
                <span className="inline-block mt-5 bg-gold/10 text-gold font-ui text-[10px] tracking-wider uppercase px-3 py-1.5">{s.tag}</span>
              </div>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Calculator */}
          <div className="border border-white/10 bg-white/[0.02] p-8">
            <h3 className="font-display smallcaps text-3xl text-white">Calculate Your Purchase</h3>
            <div className="mt-6 space-y-5">
              <div>
                <label className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Quantity (grams)</label>
                <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="e.g. 100"
                  className="w-full mt-2 bg-white/[0.03] border border-white/10 px-4 py-3 text-white font-body text-lg outline-none focus:border-gold transition-colors" />
              </div>
              <div>
                <label className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Rate per gram (₹)</label>
                <input type="number" value={rate} onChange={(e) => setRate(e.target.value)}
                  className="w-full mt-2 bg-white/[0.03] border border-white/10 px-4 py-3 text-white font-body text-lg outline-none focus:border-gold transition-colors" />
              </div>
              <div className="border border-white/8 bg-black/20 divide-y divide-white/5">
                <Row label="Silver Value" value={fmt(calc.value)} />
                <Row label="Making Charge (3%)" value={fmt(calc.making)} />
                <Row label="Total Payable" value={fmt(calc.total)} highlight />
              </div>
            </div>
          </div>

          {/* Pay & Confirm */}
          <div className="border border-white/10 bg-white/[0.02] p-8 flex flex-col">
            <h3 className="font-display smallcaps text-3xl text-white">Pay & Confirm</h3>
            <div className="flex-1 flex flex-col items-center justify-center mt-6">
              <div className="border border-dashed border-gold/40 p-3 bg-white/[0.03]">
                <div className="h-44 w-44 bg-white flex items-center justify-center text-gray-800">
                  <QrCode size={150} strokeWidth={1} />
                </div>
              </div>
              <p className="font-ui text-sm text-gray-300 mt-4 tracking-wide">Scan & Pay via UPI</p>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%20TwinBrain,%20I%20want%20to%20confirm%20my%20silver%20lease%20payment.`} target="_blank" rel="noreferrer"
                className="w-full gold-btn font-ui text-xs tracking-[0.2em] uppercase text-center px-6 py-4 mt-5 font-medium transition-all hover:shadow-lg hover:shadow-gold/20">
                Confirm Payment on WhatsApp →
              </a>
              <p className="font-body italic text-xs text-gray-500 mt-4 text-center">Pay via QR, then tap above and send your screenshot so we can confirm your lease.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Row = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between px-5 py-4">
    <span className={`font-ui text-[10px] tracking-[0.2em] uppercase ${highlight ? "text-gold" : "text-muted-foreground"}`}>{label}</span>
    <span className={`font-display ${highlight ? "text-2xl text-gold" : "text-xl text-white"}`}>{value}</span>
  </div>
);

export default BuyLease;
